// Modal.tsx — Backdrop + dialog. Clicking the scrim closes;
// Esc closes; the caller wires the action buttons.
//
// Portals into document.body so the dialog escapes any clipping
// parents. Re-uses the existing CSS classes from the legacy modal.

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Optional label for the dialog accessibility tree. */
  ariaLabel?: string;
}

export function Modal({ open, onClose, children, ariaLabel }: Props) {
  // Only treat a click as "backdrop dismiss" when BOTH mousedown and
  // mouseup landed on the backdrop. Otherwise a drag-select that starts
  // inside the dialog and releases on the scrim would close it.
  const downOnBackdropRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      className="console-modal-backdrop"
      onMouseDown={(e) => {
        downOnBackdropRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        const closeIt =
          downOnBackdropRef.current && e.target === e.currentTarget;
        downOnBackdropRef.current = false;
        if (closeIt) onClose();
      }}
    >
      <div
        className="console-modal"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
